import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as trackingLinkActions from '@redux/modules/trackingLink'

import defaultFormState, { requiredFields } from '@models/trackingLink'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'
import type { TrackingLinkModel, TrackingLinkRequestOptions } from '@models/types'

type GenerateUrlsOptions = {
  prefixRoute?: string,
}

export const generateUrls = (options?: GenerateUrlsOptions) => {
  const { prefixRoute = 'systemManager' } = options || {}

  return {
    trackingLinksIndexUrl: `#/${prefixRoute}/tracking-links`,
  }
}

type CreateTrackingLinkParams = {
  dispatch: AppDispatch,
  requestOptions?: TrackingLinkRequestOptions,
  trackingLinkParams: Partial<TrackingLinkModel>,
}

const createTrackingLink = (params: CreateTrackingLinkParams) => {
  const { dispatch, trackingLinkParams, requestOptions } = params
  const { createTrackingLink: createFn } = trackingLinkActions

  return dispatch(createFn(trackingLinkParams, requestOptions))
}

type UpdateTrackingLinkParams = {
  dispatch: AppDispatch,
  requestOptions?: TrackingLinkRequestOptions,
  trackingLink: TrackingLinkModel,
  trackingLinkParams: Partial<TrackingLinkModel>,
}

const updateTrackingLink = (params: UpdateTrackingLinkParams) => {
  const {
    dispatch, trackingLink, trackingLinkParams, requestOptions,
  } = params
  const { updateTrackingLink: updateFn } = trackingLinkActions

  const updatedParams = {
    id: trackingLink.id,
    ...trackingLinkParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteTrackingLinkParams = {
  dispatch: AppDispatch,
  trackingLink: DeleteParams<TrackingLinkModel>,
}

const deleteTrackingLink = (params: DeleteTrackingLinkParams) => {
  const { dispatch, trackingLink } = params
  const { deleteTrackingLink: deleteFn } = trackingLinkActions

  return dispatch(deleteFn(trackingLink))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useTrackingLinkForm(
  trackingLink: Partial<TrackingLinkModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const trackingLinkForm = useForm(
    defaultFormState,
    { entity: trackingLink, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [trackingLink.id, trackingLink.cache_key],
  )

  return {
    ...trackingLinkForm,
  }
}

export const useRelations = (trackingLink: Partial<TrackingLinkModel> = {}) => {
  const {
    owner_id, owner_type, subject_id, subject_type,
  } = trackingLink

  const { agencies, campaigns } = useSelector(reduxState => reduxState.entities)

  const agency = owner_type === 'Agency' && owner_id && agencies[owner_id] ? agencies[owner_id] : {}
  const campaign = subject_type === 'Campaign' && subject_id && campaigns[subject_id] ? campaigns[subject_id] : {}

  return {
    agency,
    campaign,
  }
}

function useTrackingLink(initEntity: Partial<TrackingLinkModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: trackingLink }: { entity: TrackingLinkModel} = useLatestEntity(initEntity, 'trackingLinks')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { agency, campaign } = useRelations(trackingLink)

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.trackingLinks)

  return {
    agency,
    callbacks: {
      createOrEditTrackingLink: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditTrackingLinkModal',
        payload: { trackingLink },
      }),
      createTrackingLink: (
        trackingLinkParams: Partial<TrackingLinkModel>,
        entityOptions?: TrackingLinkRequestOptions,
      ) => (
        createTrackingLink({ trackingLinkParams, dispatch, requestOptions: entityOptions })
      ),
      deleteTrackingLink: () => deleteTrackingLink({ dispatch, trackingLink }),
      updateTrackingLink: (
        trackingLinkParams: Partial<TrackingLinkModel>,
        entityOptions?: TrackingLinkRequestOptions,
      ) => (
        updateTrackingLink({
          trackingLink, trackingLinkParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    campaign,
    creating,
    deleting,
    loading: loading && !loadedIds.includes(trackingLink.id),
    trackingLink,
    updating,
    urls: generateUrls(),
  }
}

export default useTrackingLink
